import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MonoAvatar from './MonoAvatar';
import { getConversations } from '../services/messageService';
import { toDateSafe } from '../utils/dateUtils';
import '../styles/messagesTray.css';

function timeAgo(ts) {
  const d = toDateSafe(ts);
  if (!d) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function MessagesTray({ open, onClose, anchorRef }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [q, setQ] = useState('');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target) &&
          (!anchorRef?.current || !anchorRef.current.contains(e.target))) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (!open || !user?.uid) return;
    let cancelled = false;
    setLoading(true);
    getConversations(10).then((res) => {
      if (!cancelled) setThreads(res.conversations);
    }).catch(() => {}).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, user?.uid]);

  const filtered = threads.filter(t =>
    !q || (t.recipientName || '').toLowerCase().includes(q.toLowerCase()) ||
    (t.lastMessage || '').toLowerCase().includes(q.toLowerCase()));

  const unreadCount = threads.reduce((s, t) => s + (t.unread ? 1 : 0), 0);

  function openThread(t) {
    onClose();
    navigate(`/messages/${t.chatId}`, {
      state: { recipientId: t.recipientId, recipientName: t.recipientName }
    });
  }

  return (
    <div ref={ref} className={`messages-tray ${open ? 'messages-tray--open' : ''}`}>
      <div className="messages-tray__header">
        <div className="messages-tray__header-left">
          <span className="messages-tray__title">Messages</span>
          {unreadCount > 0 && (
            <span className="messages-tray__unread-pill">{unreadCount} unread</span>
          )}
        </div>
        <div className="messages-tray__header-actions">
          <button className="messages-tray__header-btn" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="messages-tray__search-wrap">
        <div className="messages-tray__search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="rgba(220,200,240,0.6)" strokeWidth="1.8"/>
            <path d="m20 20-3.5-3.5" stroke="rgba(220,200,240,0.6)" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search messages or people..."
            className="messages-tray__search-input"
          />
        </div>
      </div>

      <div className="messages-tray__list">
        {loading && threads.length === 0 ? (
          <div className="messages-tray__empty">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="messages-tray__empty">
            {q ? 'No threads match.' : 'No messages yet.'}
          </div>
        ) : (
          filtered.map(t => (
            <button key={t.chatId} className="messages-tray__row" onClick={() => openThread(t)}>
              <div className="messages-tray__row-avatar">
                <MonoAvatar name={t.recipientName || '?'} size={42} />
              </div>
              <div className="messages-tray__row-content">
                <div className="messages-tray__row-top">
                  <span className="messages-tray__row-name">
                    {t.recipientName}
                  </span>
                  <span className="messages-tray__row-time">
                    {timeAgo(t.lastMessageAt)}
                  </span>
                </div>
                <span className="messages-tray__row-preview">
                  {t.lastMessage}
                </span>
              </div>
              {t.unread && (
                <span className="messages-tray__row-badge" />
              )}
            </button>
          ))
        )}
      </div>

      <div className="messages-tray__footer">
        <button className="messages-tray__footer-btn" onClick={() => { onClose(); navigate('/messages'); }}>
          <span>See all messages</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
